import React, { useState } from "react";
import { Button, Input, Slider } from "antd";
import { isNil, trim } from "lodash";
import { EComModal } from "components/EComModal";
import { aiccTtsSettingApi } from "services/aiccLearningAssets";
import { AiccTtsTarget } from "types/model/aiccLearningAssets";
import {
  BASE_SPEED_BOUNDS,
  BASE_SPEED_STEP,
  DEFAULT_BASE_SPEED,
  formatBaseSpeed,
  snapToStep,
  TTS_TARGET_LABEL,
} from "./configs";
import styles from "./TtsSetting.module.scss";

interface Props {
  languageId?: number;
  voiceId: string;
  target: AiccTtsTarget;
  model?: string;
  /** The model's name as the page shows it. */
  modelLabel: string;
  /** The model's `supportsStreamPlay`, from the backend's options; undefined when it sent none. */
  streaming?: boolean;
  currentBaseSpeed?: number;
  onCancel: () => void;
  onSave: (baseSpeed: number) => void;
}

interface SynthesizedFrom {
  baseSpeed: number;
  text: string;
}

function AdjustBaseSpeedModal({
  languageId,
  voiceId,
  target,
  model,
  modelLabel,
  streaming,
  currentBaseSpeed,
  onCancel,
  onSave,
}: Props) {
  const [text, setText] = useState("");
  const [baseSpeed, setBaseSpeed] = useState(snapToStep(currentBaseSpeed ?? DEFAULT_BASE_SPEED));
  const [audioUrl, setAudioUrl] = useState<string>();
  const [synthesizedFrom, setSynthesizedFrom] = useState<SynthesizedFrom>();
  const [isRunning, setIsRunning] = useState(false);

  const isStale = !!synthesizedFrom && (synthesizedFrom.baseSpeed !== baseSpeed || synthesizedFrom.text !== text);

  const run = () => {
    if (isNil(languageId) || !trim(text)) {
      return;
    }
    const requestedWith = { baseSpeed, text };
    setIsRunning(true);
    setAudioUrl(undefined);
    setSynthesizedFrom(undefined);
    aiccTtsSettingApi
      .synthesize(languageId, { voiceId, text, baseSpeed, model, streaming })
      .then((url) => {
        setAudioUrl(url);
        setSynthesizedFrom(requestedWith);
      })
      .catch(() => undefined)
      .finally(() => setIsRunning(false));
  };

  return (
    <EComModal
      visible
      width={720}
      title="Adjust Base Speed"
      okText={`Use ${formatBaseSpeed(baseSpeed)}`}
      okButtonProps={{ disabled: !audioUrl || isStale }}
      onOk={() => onSave(baseSpeed)}
      onCancel={onCancel}
    >
      <div className={styles.modalBody}>
        <div className={styles.voiceInfo}>
          <div>
            {"Voice ID: "}
            <span className={styles.voiceInfoValue}>{voiceId}</span>
          </div>
          <div>
            {"Target: "}
            <span className={styles.voiceInfoValue}>{TTS_TARGET_LABEL[target]}</span>
          </div>
          <div>
            {"Model: "}
            <span className={styles.voiceInfoValue}>{modelLabel}</span>
          </div>
          <div>
            {"Current: "}
            <span className={styles.voiceInfoValue}>
              {isNil(currentBaseSpeed)
                ? `not calibrated (${formatBaseSpeed(DEFAULT_BASE_SPEED)})`
                : formatBaseSpeed(currentBaseSpeed)}
            </span>
          </div>
        </div>
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Test text</div>
          <Input.TextArea
            rows={6}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Enter the text to synthesize for evaluation…"
          />
        </div>
        <div className={styles.field}>
          <div className={styles.fieldHeader}>
            <span className={styles.fieldLabel}>Base speed</span>
            <span className={styles.baseSpeedValue}>{formatBaseSpeed(baseSpeed)}</span>
          </div>
          <div className={styles.fieldHint}>
            {"Drag to set this voice’s baseline pace, then run to hear it. The learner’s own speed choice still " +
              "applies on top of whatever you pick here."}
          </div>
          <Slider
            className={styles.slider}
            min={BASE_SPEED_BOUNDS.min}
            max={BASE_SPEED_BOUNDS.max}
            step={BASE_SPEED_STEP}
            tooltipVisible={false}
            value={baseSpeed}
            onChange={(value: number) => setBaseSpeed(snapToStep(value))}
          />
          <div className={styles.sliderBounds}>
            <span>{`${formatBaseSpeed(BASE_SPEED_BOUNDS.min)} — slower`}</span>
            <span>{`faster — ${formatBaseSpeed(BASE_SPEED_BOUNDS.max)}`}</span>
          </div>
        </div>
        <div className={styles.preview}>
          <Button type="primary" shape="round" loading={isRunning} disabled={!trim(text)} onClick={run}>
            Run
          </Button>
          {audioUrl && (
            <audio
              className={styles.audio}
              controls
              controlsList="nodownload noplaybackrate noremoteplayback"
              src={audioUrl}
            />
          )}
          {synthesizedFrom && (
            <span className={styles.generatedHint}>
              {"Generated at base speed "}
              <span className={styles.voiceInfoValue}>{formatBaseSpeed(synthesizedFrom.baseSpeed)}</span>
            </span>
          )}
          {isStale && <span className={styles.staleHint}>Speed or text changed — run again to save.</span>}
        </div>
      </div>
    </EComModal>
  );
}

export default AdjustBaseSpeedModal;
